import React, { FC, useState, useRef } from 'react';
import translate from '@translate';
import ProductsSelect from 'src/components/ProductsSelect';
import { checkFormValidity } from 'src/utils/formFunctions';
import TextField from 'src/components/TextField';
import Grid, { GridItem } from 'src/components/Grid';
import Button from 'src/components/Button';
import Typography from 'src/components/Typography';
import { colors, sizes } from 'src/themes/default';
import { FormErrorsType } from '../../types/global';
import { ProductSetItemType } from 'src/types/product';

type NewProductSetItemBlockPropsType = {
    data?: ProductSetItemType[];
    onChange: (value: ProductSetItemType[]) => void;
    disableEdit?: boolean;
    editErrors: FormErrorsType;
};

type EditKeysType = 'PRODUCT_COUNT' | 'PRODUCT_SKU';

const initData: ProductSetItemType = {
    PRODUCT_SKU: '',
    PRODUCT_COUNT: 1,
};

const NewProductSetItemBlock: FC<NewProductSetItemBlockPropsType> = (props) => {
    const formRef = useRef<HTMLFormElement>(null);

    const [editData, setEditData] = useState<ProductSetItemType>(initData);
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});

    const changeValue = (key: EditKeysType, value: string | number, error: boolean) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        if (key === 'PRODUCT_SKU') {
            newData.PRODUCT_SKU = value as string;
        } else if (key === 'PRODUCT_COUNT') {
            newData.PRODUCT_COUNT = value as number;
        }
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleAddRow = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (!Object.keys(newFormErrors).length) {
            const existingIds = (props.data || []).map((item) => item.ID || 0);
            const newId = Math.min(0, ...existingIds) - 1;

            props.onChange([
                ...(props.data || []),
                {
                    ...editData,
                    ID: newId,
                },
            ]);

            setEditData(initData);
        }

        setEditErrors(newFormErrors);
    };

    return (
        <>
            <Typography gutterBottom variant="h3" sx={props.editErrors.PRODUCT_SET ? { color: colors.alert_O } : {}}>
                {translate('productDetail.productSetTab.addProduct')}
            </Typography>
            <form ref={formRef}>
                <Grid>
                    <GridItem cols={8}>
                        <ProductsSelect
                            value={editData.PRODUCT_SKU}
                            error={editErrors.PRODUCT_SKU}
                            idKey="SKU"
                            onChange={(value, error) => {
                                if (!Array.isArray(value)) {
                                    changeValue('PRODUCT_SKU', value?.SKU, error);
                                }
                            }}
                            name="PRODUCT_SKU"
                            label={translate('productDetail.productSetTab.productSku')}
                            fullWidth
                            disabled={props.disableEdit}
                            required
                        />
                    </GridItem>
                    <GridItem cols={2}>
                        <TextField
                            value={editData.PRODUCT_COUNT}
                            error={editErrors.PRODUCT_COUNT}
                            onChange={(value, error) => changeValue('PRODUCT_COUNT', value || 1, error)}
                            name="PRODUCT_COUNT"
                            label={translate('productDetail.productSetTab.quantity')}
                            fullWidth
                            required
                            type="numberLocale"
                            min={1}
                            disabled={props.disableEdit}
                        />
                    </GridItem>
                    <GridItem cols={2}>
                        <Button
                            onClick={handleAddRow}
                            disabled={props.disableEdit}
                            sx={{ marginTop: sizes.input_top_PD }}
                            mobileFullWidht
                        >
                            {translate('productDetail.productSetTab.add')}
                        </Button>
                    </GridItem>
                </Grid>
            </form>
        </>
    );
};

export default NewProductSetItemBlock;
